import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { apiDefaultFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  DeliveryStatsResponse,
  DeliveryStatsVars,
  GetEmbedTagsResponse,
  GetEmbedTagsVars,
  GetMasterSummaryResponse,
  GetMasterSummaryVars,
  GetModelTuningResponse,
  GetModelTuningVars,
  GetSiteResponse,
  GetSiteVars,
  ListSitesResponse,
  ListSitesVars,
  PlacementStatsResponse,
  PlacementStatsVars,
  SiteApiPath,
  SiteDashboardResponse,
  SiteDashboardVars
} from '@/common/api/params/site'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'

export const useSiteGetApi = (vars: GetSiteVars | undefined) => {
  return useSWR<GetSiteResponse, Response, [string, GetSiteVars] | undefined>(
    vars ? [SiteApiPath.GET_SITE, vars] : undefined
  )
}

// 2026-09-13 追加: 配信ごとの埋め込みタグ（メール用タグを含む）
export const useEmbedTagsGetApi = (vars: GetEmbedTagsVars | undefined) => {
  return useSWR<GetEmbedTagsResponse, Response, [string, GetEmbedTagsVars] | undefined>(
    vars ? [SiteApiPath.GET_EMBED_TAGS, vars] : undefined
  )
}

export const useSiteListApi = (vars: ListSitesVars) => {
  return useSWR<ListSitesResponse, Response, [string, ListSitesVars]>([
    SiteApiPath.LIST_SITES,
    vars
  ])
}

export const useSiteDashboardGetApi = (vars: SiteDashboardVars) => {
  return useSWR<SiteDashboardResponse, Response, [string, SiteDashboardVars]>([
    SiteApiPath.SITE_DASHBOARD,
    vars
  ])
}

export const useDeliveryStatsGetApi = (vars: DeliveryStatsVars) => {
  return useSWR<DeliveryStatsResponse, Response, [string, DeliveryStatsVars]>([
    SiteApiPath.DELIVERY_STATS,
    vars
  ])
}

// 設置場所（ページ）別の配信実績。openspec: recommend-placement-cv
export const usePlacementStatsGetApi = (vars: PlacementStatsVars) => {
  return useSWR<PlacementStatsResponse, Response, [string, PlacementStatsVars]>([
    SiteApiPath.PLACEMENT_STATS,
    vars
  ])
}

export const useMasterSummaryGetApi = (vars: GetMasterSummaryVars) => {
  return useSWR<GetMasterSummaryResponse, Response, [string, GetMasterSummaryVars]>([
    SiteApiPath.GET_MASTER_SUMMARY,
    vars
  ])
}

export const useModelTuningGetApi = (vars: GetModelTuningVars) => {
  return useSWR<GetModelTuningResponse, Response, [string, GetModelTuningVars]>([
    SiteApiPath.GET_MODEL_TUNING,
    vars
  ])
}

export const convertConditionToSiteSearchListVars = (
  condition: SiteSearchCondition
): ListSitesVars => {
  return {
    page: condition.page,
    per_page: condition.perPage,
    ...(condition.keyword && {
      search: condition.keyword
    }),
    ...(condition.orderBy && {
      sort_by: condition.orderBy.split('-')[0],
      sort_order: condition.orderBy.split('-')[1]
    })
  }
}

export const useSiteSearchApi = (condition: SiteSearchCondition) => {
  return useSWR<ListSitesResponse, Response, [string, ListSitesVars]>(
    [
      SiteApiPath.LIST_SITES,
      {
        ...convertConditionToSiteSearchListVars(condition)
      }
    ],
    apiDefaultFetcher
  )
}

export const SiteOrderBy = {
  NAME_ASC: 'name-asc',
  NAME_DESC: 'name-desc',
  CREATED_AT_ASC: 'created-at-asc',
  CREATED_AT_DESC: 'created-at-desc',
  UPDATED_AT_ASC: 'updated-at-asc',
  UPDATED_AT_DESC: 'updated-at-desc'
} as const

export type SiteOrderBy = (typeof SiteOrderBy)[keyof typeof SiteOrderBy]

export const useSiteSearchCondition = (): [
  SiteSearchCondition,
  Dispatch<SetStateAction<SiteSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<SiteSearchCondition>(siteSearchConditionParam)
}

export interface SiteSearchCondition extends SearchConditionBase {
  keyword: string | null
  orderBy: SiteOrderBy | null
}

const siteSearchConditionParam: SearchConditionParam[] = [
  {
    name: 'keyword',
    type: 'string'
  },
  {
    name: 'orderBy',
    type: 'string'
  }
]

export const SITE_SEARCH_CONDITION_CLEAR: SiteSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  orderBy: null
}
